/**
 * Authentication utilities
 * JWT token parsing and organization context management
 */

interface TokenPayload {
  user_id: number;
  cbo_id?: number;
  exp?: number;
}

/**
 * Extract org ID from URL path
 * Supports patterns like /organizations/cbo/123 or /cbo/123
 */
export function getOrgIdFromUrl(): number | null {
  const path = window.location.pathname;

  // Match /organizations/cbo/{id} or /cbo/{id}
  const match = path.match(/\/(?:organizations\/)?cbo\/(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }

  // Also check URL search params
  const params = new URLSearchParams(window.location.search);
  const cboId = params.get('cbo_id') || params.get('org_id');
  if (cboId) {
    return parseInt(cboId, 10);
  }

  return null;
}

/**
 * Update URL to reflect current org ID without full page reload
 */
export function syncUrlWithOrgId(cboId: number): void {
  const currentUrl = new URL(window.location.href);
  const path = currentUrl.pathname;

  // Update path if it contains org pattern
  const match = path.match(/\/(?:organizations\/)?cbo\/\d+/);
  if (match) {
    const newPath = path.replace(/\/(?:organizations\/)?cbo\/\d+/, `/organizations/cbo/${cboId}`);
    if (newPath !== path) {
      window.history.replaceState({}, '', newPath + currentUrl.search);
    }
  }
}

/**
 * Sync org ID from URL to localStorage on page load
 */
export function syncOrgIdFromUrl(): boolean {
  const urlOrgId = getOrgIdFromUrl();

  if (urlOrgId) {
    const storedOrgId = localStorage.getItem('active_cbo_id');

    if (storedOrgId !== urlOrgId.toString()) {
      localStorage.setItem('active_cbo_id', urlOrgId.toString());
      return true; // Indicates a change was made
    }
  }

  return false;
}

/**
 * Decode JWT token and return payload
 */
export function decodeJWT(token: string): TokenPayload | null {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode base64url payload
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Initialize active CBO ID and user ID from URL or JWT token
 * Priority: URL > existing localStorage > JWT token
 * This ensures API calls include the correct X-CBO-ID header
 */
export function initializeActiveCboId(): void {
  // First, check if URL has an org ID and sync it
  const urlOrgId = getOrgIdFromUrl();
  if (urlOrgId) {
    const existingCboId = localStorage.getItem('active_cbo_id');
    if (existingCboId !== urlOrgId.toString()) {
      localStorage.setItem('active_cbo_id', urlOrgId.toString());
    }
  }

  // Get auth token to extract user_id
  const token = localStorage.getItem('auth_token');
  if (!token) {
    return;
  }

  // Decode token and extract cbo_id and user_id
  const payload = decodeJWT(token);
  if (!payload) {
    return;
  }

  // Always store user_id from token
  if (payload.user_id) {
    localStorage.setItem('user_id', payload.user_id.toString());
  }

  // Handle cbo_id if not already set from URL
  if (!urlOrgId) {
    const existingCboId = localStorage.getItem('active_cbo_id');
    if (existingCboId) {
      return;
    }

    if (payload.cbo_id) {
      localStorage.setItem('active_cbo_id', payload.cbo_id.toString());
    }
    // No cbo_id in the token (e.g. a super_admin / ops_admin with no home org):
    // deliberately leave active_cbo_id UNSET rather than hard-defaulting to '1'.
    // A hardcoded '1' pinned these users to organization 1 on the web app — and,
    // because a super_admin's org list contains org 1, OrganizationContext then
    // resolved that stale value as the "active" org and never fell through to
    // their real/selected organization. The mobile staff-app has no such default
    // (it derives the org from the user's own list: primaryOrg || organizations[0]),
    // so the two clients showed different orgs' data for the same user — the
    // "data not synced between mobile and desktop" report (TRUS-435). Leaving it
    // unset lets OrganizationContext resolve the correct org asynchronously, in
    // parity with mobile.
  }
}

/**
 * Get current user ID from localStorage or JWT token
 */
export function getCurrentUserId(): number | null {
  // First check localStorage
  const storedUserId = localStorage.getItem('user_id');
  if (storedUserId) {
    return parseInt(storedUserId, 10);
  }

  // Fall back to decoding JWT
  const token = localStorage.getItem('auth_token');
  if (token) {
    const payload = decodeJWT(token);
    if (payload?.user_id) {
      // Store it for future use
      localStorage.setItem('user_id', payload.user_id.toString());
      return payload.user_id;
    }
  }

  return null;
}

/**
 * Clear authentication data
 */
export function clearAuth(): void {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('active_cbo_id');
  localStorage.removeItem('user_id');
}
